import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { FeatureService } from './feature.service';
import { IframeCoordinatorClientService } from './iframe-coordinator-client.service';

/**
 * Determines the user's language based on cookies and window.navigator settings
 * and sets the language for use in the translation service and Genesys web components
 */
@Injectable({
  providedIn: 'root'
})
export class LanguageService implements OnDestroy {
  private subscription: Subscription;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly translate: TranslateService,
    private readonly iframe: IframeCoordinatorClientService,
    private readonly feature: FeatureService
  ) {
    // Set fallback language for translation service
    this.translate.setDefaultLang('en');
  }

  public get language(): string {
    return this.translate.currentLang;
  }

  public init(): void {
    this.subscription = this.iframe.envData$
      .pipe(
        map((env) => env.locale),
        distinctUntilChanged()
      )
      .subscribe((locale) => {
        this.setLanguage(locale);
      });

    this.feature.hasFeature('adminUI.v3.IFC.migration').subscribe((hasIFC) => {
      if (!hasIFC) {
        // Deprecated locale fetching
        this.setLanguage(this.getLanguage());
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** Set default language and current language for localization features */
  private setLanguage(language: string): void {
    // Setup language for the translation service
    this.translate.use(language);

    // Setup lang attribute for the localization of Genesys web components
    this.setLanguageToHtml(language);
  }

  /** Sets the language to to the lang attribute of the html tag */
  private setLanguageToHtml(language: string): void {
    this.document.getElementsByTagName('html')[0].setAttribute('lang', language);
  }

  /**
   * Checks for language setting in cookie or window.navigator
   *
   * @deprecated
   */
  private getLanguage(): string {
    const language = this.getLanguageFromCookie() || this.getLanguageFromNavigator();

    if (!language) {
      console.warn('No language preference found, defaulting to english');
    }

    return language;
  }

  /**
   * Checks for language setting in userPrefLangTag cookie.
   * Will return undefined if the cookie does not exist.
   *
   * @deprecated
   */
  private getLanguageFromCookie(): string {
    return this.document.cookie
      ?.split(';')
      ?.find((cookie) => !!cookie.match(/userPrefLangTag/))
      ?.split('=')[1];
  }

  /**
   * Checks for language in window.navigator.
   * Will return undefined if languages, language, and userLanguage properties are all falsey values.
   *
   * @deprecated
   */
  private getLanguageFromNavigator(): string {
    const { languages, language, userLanguage } = <INavigatorUserLanguage>(
      this.document.defaultView.navigator
    );
    const lang = Array.isArray(languages) ? languages[0] : undefined;
    const navLocale = lang || language || userLanguage;

    return navLocale ? navLocale.toLowerCase() : undefined;
  }
}

interface INavigatorUserLanguage extends Navigator {
  userLanguage?: string;
}
